import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AppLink from '../generic/AppLink';
import { getStartedPricingPage } from '../../Constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

const cellSx = {
  px: 2.5,
  py: 2,
  borderTop: '1px solid #202020',
  borderLeft: '1px solid #202020',
  verticalAlign: 'middle',
  '&:first-of-type': {
    borderLeft: 'none',
  },
};

const comparisonRows = [
  {
    feature: 'Trading Accounts',
    description: 'Trading accounts in your TradesPad account',
    free: '1',
    basic: '2',
    pro: 'Unlimited',
  },
  {
    feature: 'Backtesting',
    description: 'Go back in time with our trading simulator in TradingView charts',
    free: { soon: true, note: 'Up to 1 Session, no journaling and limited analytics' },
    basic: { soon: true, note: 'Unlimited Sessions and analytics, no journaling' },
    pro: { soon: true, note: 'Unlimited Sessions, analytics and journaling' },
  },
  /* {
    feature: 'Photo Uploading',
    description: 'Upload screenshots in your journaled trades',
    free: '500MB & 1 photo per trade',
    basic: 'Unlimited',
    pro: 'Unlimited',
  }, */
  {
    feature: 'Strategies',
    description: 'Create strategies with rules and use them in your trades',
    free: '1',
    basic: '2',
    pro: 'Unlimited',
  },
  {
    feature: 'Manual Import',
    description: 'Add and journal your trades manually to TradesPad',
    free: true,
    basic: true,
    pro: true,
  },
  {
    feature: 'Auto-Sync Trades',
    description: 'Sync your trades automatically from your broker to TradesPad in 1 click',
    free: false,
    basic: true,
    pro: true,
  },
  {
    feature: 'Automatic Analytics',
    description: 'All analytics needed for improve your results in a visual and minimalistic interface',
    free: true,
    basic: true,
    pro: true,
  },
  {
    feature: 'Economic Calendar',
    description: 'Economic calendar filtered by currency and impact in a visual calendar format',
    free: true,
    basic: true,
    pro: true,
  },
  {
    feature: 'Communities',
    description: 'Join or create communities to share your trades with others',
    free: { value: '1', subtext: 'Soon' },
    basic: { value: '2', subtext: 'Soon' },
    pro: { value: 'Unlimited', subtext: 'Soon' },
  },
  {
    feature: 'Trade Replay',
    description: 'Replay your trading days and trades in TradingView Charts',
    free: false,
    basic: false,
    pro: 'Soon',
  },
  {
    feature: 'Mobile App',
    description: 'Take your journal everywhere with TradesPad Mobile',
    free: 'Soon',
    basic: 'Soon',
    pro: 'Soon',
  },
];

const tiers = [
  {
    title: 'Free',
    price: '0',
    annualPrice: '0',
    features: [
      { text: 'Up to 1 trading account', enabled: true },
      { text: 'Up to 1 backtesting session (Soon)', enabled: true },
      { text: 'Automatic Analytics', enabled: true },
      { text: 'Up to 1 strategy', enabled: true },
      { text: 'Up to 1 community (Soon)', enabled: true },
      { text: 'Auto-Sync your trades', enabled: false },
      { text: 'Trade Replay (Soon)', enabled: false },
      { text: 'Backtesting Journaling', enabled: false },
    ],
    buttonText: 'Get Started',
    buttonVariant: 'outlined',
  },
  {
    title: 'Pro',
    price: '19',
    annualPrice: '17',
    originalPrice: '29',
    special: true,
    chip: 'Early Access offer',
    subtitle: 'Limited to 200 spots, price locked forever',
    features: [
      { text: 'Unlimited trading accounts', enabled: true },
      { text: 'Auto-Sync your trades', enabled: true },
      { text: 'Unlimited Backtesting (Soon)', enabled: true },
      { text: 'Automatic Analytics', enabled: true },
      { text: 'Trade Replay (Soon)', enabled: true },
      { text: 'Unlimited Strategies', enabled: true },
      { text: 'Unlimited Communities (Soon)', enabled: true },
    ],
    buttonText: 'Get Started',
    buttonVariant: 'contained',
  },
  {
    title: 'Basic',
    price: '19',
    annualPrice: '17',
    features: [
      { text: 'Up to 2 trading accounts', enabled: true },
      { text: 'Auto-Sync your trades', enabled: true },
      { text: 'Unlimited Backtesting Sessions (Soon)', enabled: true },
      { text: 'Automatic Analytics', enabled: true },
      { text: 'Up to 2 trading strategies', enabled: true },
      { text: 'Up to 2 communities (Soon)', enabled: true },
      { text: 'Trade Replay (Soon)', enabled: false },
      { text: 'Backtesting Journaling', enabled: false },
    ],
    buttonText: 'Get Started',
    buttonVariant: 'outlined',
  },
];

export default function Pricing() {
  const [annual, setAnnual] = React.useState(true);

  return (
    <Container
      id="pricing"
      sx={{
        pt: { xs: 4, sm: 8 },
        pb: { xs: 4, sm: 8 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 1, sm: 2 },
      }}
    >
      <Box
        sx={{
          width: { sm: '100%', md: '60%' },
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            color: '#797979',
            fontSize: '1rem',
            mb: 1,
          }}
        >
          Pricing
        </Typography>
        <Typography
          component="h2"
          variant="h3"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            pb: { xs: 3, sm: 5 },
            fontSize: { xs: '2rem', sm: '2.75rem' },
          }}
        >
          Choose a plan that
          <br />
          works for you
        </Typography>

        {/* Monthly / Yearly toggle */}
        <Box
          sx={{
            display: 'inline-flex',
            borderRadius: '8px',
            border: '1px solid #333',
            overflow: 'hidden',
            mb: { xs: 4, sm: 6 },
          }}
        >
          <Button
            onClick={() => setAnnual(false)}
            sx={{
              px: 3,
              py: 1,
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: !annual ? '#FFF' : '#777',
              backgroundColor: !annual ? '#222' : 'transparent',
              border: !annual ? '1px solid #333' : '1px solid transparent',
              '&:hover': {
                backgroundColor: !annual ? '#333' : 'rgba(255,255,255,0.05)',
                border: !annual ? '1px solid #333' : '1px solid transparent',
              },
            }}
          >
            Monthly
          </Button>
          <Button
            onClick={() => setAnnual(true)}
            sx={{
              px: 3,
              py: 1,
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: annual ? '#FFF' : '#777',
              backgroundColor: annual ? '#222' : 'transparent',
              border: annual ? '1px solid #333' : '1px solid transparent',
              '&:hover': {
                backgroundColor: annual ? '#333' : 'rgba(255,255,255,0.05)',
                border: annual ? '1px solid #333' : '1px solid transparent',
              },
            }}
          >
            Yearly (Save 10%)
          </Button>
        </Box>
      </Box>

      <Grid
        container
        spacing={3}
        sx={{ alignItems: 'stretch', justifyContent: 'center', width: '100%', position: 'relative', zIndex: 1 }}
      >
        {tiers.map((tier) => (
          <Grid
            size={{ xs: 12, sm: 6, md: 4 }}
            key={tier.title}
          >
            <Card
              sx={{
                px: 2.5,
                py: 3,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                backgroundColor: tier.special ? '#0F0F0F' : 'transparent',
                border: tier.special ? '1px solid #202020' : '2px dashed #202020',
                borderRadius: '16px',
                backgroundImage: 'none',
                boxShadow: 'none',
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 0, '&:last-child': { pb: 0 } }}>
                {/* Title row with optional chip */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography
                    component="h3"
                    sx={{
                      color: '#FFF',
                      fontSize: '1.35rem',
                      fontWeight: 500,
                    }}
                  >
                    {tier.title}
                  </Typography>
                  {tier.chip && (
                    <Chip
                      icon={<AttachMoneyIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                      label={tier.chip}
                      size="small"
                      sx={{
                        backgroundColor: '#000000',
                        color: '#FFF',
                        border: '1px solid #353535',
                        borderRadius: '6px',
                        fontSize: '0.7rem',
                        px: 0.5,
                        py: 1.5,
                        '& .MuiChip-label': {
                          color: '#FFF',
                        },
                      }}
                    />
                  )}
                </Box>

                {/* Price */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                  {tier.originalPrice && (
                    <Typography
                      sx={{
                        color: '#555',
                        fontSize: '1.6rem',
                        fontWeight: 500,
                        textDecoration: 'line-through',
                        mr: 1,
                      }}
                    >
                      ${tier.originalPrice}
                    </Typography>
                  )}
                  <Typography
                    component="span"
                    sx={{
                      color: '#FFF',
                      fontSize: '2.75rem',
                      fontWeight: 600,
                      lineHeight: 1,
                    }}
                  >
                    ${annual ? tier.annualPrice : tier.price}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: '#666',
                      fontSize: '0.95rem',
                      ml: 0.75,
                      fontWeight: 400,
                    }}
                  >
                    /per month
                  </Typography>
                </Box>

                {/* Features */}
                {tier.features.map((feature) => (
                  <Box
                    key={feature.text}
                    sx={{ py: 0.75, display: 'flex', gap: 1.5, alignItems: 'center' }}
                  >
                    <Box sx={{ width: 18, color: feature.enabled ? '#FFF' : '#616161', display: 'flex', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faCheck} size="sm" />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: feature.enabled ? '#FFF' : '#616161',
                        textDecoration: feature.enabled ? 'none' : 'line-through',
                        fontWeight: feature.enabled ? 500 : 400,
                      }}
                    >
                      {feature.text}
                    </Typography>
                  </Box>
                ))}

                {/* Subtitle */}
                {tier.subtitle && (
                  <Typography
                    sx={{
                      color: '#666',
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                      position: 'relative',
                      top: 30,
                      textAlign: 'center',
                    }}
                  >
                    {tier.subtitle}
                  </Typography>
                )}
              </CardContent>

              <CardActions sx={{ p: 0, mt: 2 }}>
                <AppLink href={getStartedPricingPage} style={{ width: '100%' }}>
                  <Button
                    fullWidth
                    variant={tier.buttonVariant}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '10px',
                      py: 1.25,
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      ...(tier.special
                        ? {
                            backgroundColor: '#FFF',
                            color: '#000',
                            '&:hover': {
                              backgroundColor: '#E0E0E0',
                            },
                          }
                        : {
                            backgroundColor: '#0F0F0F',
                            border: '1px solid #202020',
                            color: '#CCC',
                            '&:hover': {
                              backgroundColor: '#1A1A1A',
                              border: '1px solid #202020',
                            },
                          }),
                    }}
                  >
                    {tier.buttonText}
                  </Button>
                </AppLink>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Full Comparison Table */}
      <Box sx={{ width: '100%', mt: { xs: 6, sm: 8 }, position: 'relative', zIndex: 1 }}>
        <Typography
          sx={{
            color: '#797979',
            fontSize: '1rem',
            textAlign: 'center',
            mb: 4,
          }}
        >
          Full Comparison
        </Typography>

        <Box
          sx={{
            border: '1px solid #202020',
            borderRadius: '16px',
            overflow: 'hidden',
          }}
        >
          {/* Table */}
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="thead">
              <Box component="tr">
                <Box component="th" sx={{ ...cellSx, borderTop: 'none', width: '22%' }} />
                <Box component="th" sx={{ ...cellSx, borderTop: 'none', width: '26%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: '#FFF', fontWeight: 600, fontSize: '1.1rem' }}>Free</Typography>
                    <Typography sx={{ color: '#797979', fontSize: '0.85rem', fontWeight: 500 }}>0$ a month</Typography>
                  </Box>
                </Box>
                <Box component="th" sx={{ ...cellSx, borderTop: 'none', width: '26%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: '#FFF', fontWeight: 600, fontSize: '1.1rem' }}>Pro</Typography>
                    <Typography sx={{ color: '#797979', fontSize: '0.85rem', fontWeight: 500 }}>29$ a month</Typography>
                  </Box>
                </Box>
                <Box component="th" sx={{ ...cellSx, borderTop: 'none', width: '26%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: '#FFF', fontWeight: 600, fontSize: '1.1rem' }}>Basic</Typography>
                    <Typography sx={{ color: '#797979', fontSize: '0.85rem', fontWeight: 500 }}>19$ a month</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box component="tbody">
              {comparisonRows.map((row, i) => (
                <Box component="tr" key={i}>
                  <Box component="td" sx={cellSx}>
                    <Typography sx={{ color: '#FFF', fontWeight: 600, fontSize: '0.85rem' }}>{row.feature}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.75rem', mt: 0.25, fontWeight: 500 }}>{row.description}</Typography>
                  </Box>
                  {[row.free, row.pro, row.basic].map((val, j) => (
                    <Box component="td" key={j} sx={{ ...cellSx, textAlign: 'center' }}>
                      {val === true ? (
                        <Typography sx={{ color: '#FFF', fontSize: '1.1rem' }}>
                          <FontAwesomeIcon icon={faCheck} size="xs" />
                        </Typography>
                      ) : val === false ? (
                        <Typography sx={{ color: '#555', fontSize: '1.1rem' }}>–</Typography>
                      ) : typeof val === 'object' ? (
                        <>
                          {val.value !== undefined ? (
                            <Typography sx={{ color: '#FFF', fontWeight: 600, fontSize: '0.95rem' }}>{val.value}</Typography>
                          ) : (
                            <Typography sx={{ color: '#FFF', fontSize: val.soon ? '0.95rem' : '1.1rem', fontWeight: val.soon ? 600 : 'normal' }}>
                              {val.soon ? 'Soon' : <FontAwesomeIcon icon={faCheck} size="xs" />}
                            </Typography>
                          )}
                          <Typography sx={{ color: '#666', fontSize: '0.8rem', mt: 0.25 }}>
                            {val.note || val.subtext}
                          </Typography>
                        </>
                      ) : (
                        <Typography sx={{ color: '#FFF', fontWeight: 600, fontSize: '0.95rem' }}>{val}</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
